import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "AppInsights";
import GlobalMeta from "components/components/Meta/GlobalMeta";
import SuspenseWrapper from "components/components/SuspenseWrapper/SuspenseWrapper";
import Layout from "components/layout/Layout/Layout";
import ScrollToTop from "components/layout/ScrollToTop/ScrollToTop";
import { ErrorProvider } from "contexts/ErrorContext";
import ActualGenerationPerType from "pages/ActualGenerationPerType/ActualGenerationPerType";
import ActualOrEstimatedWindAndSolarPowerGeneration from "pages/ActualOrEstimatedWindAndSolarPowerGeneration/ActualOrEstimatedWindAndSolarPowerGeneration";
import AdjustmentActionsDisbsad from "pages/AdjustmentActionsDisbsad/AdjustmentActionsDisbsad";
import AdjustmentDataNetbsad from "pages/AdjustmentDataNetbsad/AdjustmentDataNetbsad";
import Balancing from "pages/Balancing/Balancing";
import BmuView from "pages/BmuView/BmuView";
import CreditDefaultNotices from "pages/CreditDefaultNotices/CreditDefaultNotices";
import DailyEnergyTransmitted from "pages/DailyEnergyTransmitted/DailyEnergyTransmitted";
import DataServices from "pages/DataServices/DataServices";
import DayAheadAggregatedGeneration from "pages/DayAheadAggregatedGeneration/DayAheadAggregatedGeneration";
import Demand from "pages/Demand/Demand";
import DemandForecast from "pages/DemandForecast/DemandForecast";
import DemandOutturn from "pages/DemandOutturn/DemandOutturn";
import DerivedData from "pages/DerivedData/DerivedData";
import DetailedSystemPrices from "pages/DetailedSystemPrices/DetailedSystemPrices";
import Events from "pages/Events/Events";
import Generation from "pages/Generation/Generation";
import GenerationByFuelType from "pages/GenerationByFuelType/GenerationByFuelType";
import GenerationForecast from "pages/GenerationForecast/GenerationForecast";
import GenerationForecastForWindAndSolar from "pages/GenerationForecastForWindAndSolar/GenerationForecastForWindAndSolar";
import Homepage from "pages/Homepage/Homepage";
import IndicatedForecasts from "pages/IndicatedForecasts/IndicatedForecasts";
import IndicativeDemandPeak from "pages/IndicativeDemandPeak/IndicativeDemandPeak";
import InterconnectorFlows from "pages/InterconnectorFlows/InterconnectorFlows";
import Iris from "pages/Iris/Iris";
import Lolpdrm from "pages/Lolpdrm/Lolpdrm";
import MarketIndexPrices from "pages/MarketIndexPrices/MarketIndexPrices";
import MarketView from "pages/MarketView/MarketView";
import MyInsights from "pages/MyInsights/MyInsights";
import NotFoundPage from "pages/NotFoundPage/NotFoundPage";
import Notices from "pages/Notices/Notices";
import Profile from "pages/Profile/Profile";
import Remit from "pages/Remit/Remit";
import RemitMessageDetails from "pages/RemitMessageDetails/RemitMessageDetails";
import RemitSubmit from "pages/RemitSubmit/RemitSubmit";
import RestorationZoneDemandForecast from "pages/RestorationZoneDemandForecast/RestorationZoneDemandForecast";
import RestorationZoneDemandRestored from "pages/RestorationZoneDemandRestored/RestorationZoneDemandRestored";
import RollingSystemDemand from "pages/RollingSystemDemand/RollingSystemDemand";
import RollingSystemFrequency from "pages/RollingSystemFrequency/RollingSystemFrequency";
import SettlementMessages from "pages/SettlementMessages/SettlementMessages";
import ShortTermOperatingReserves from "pages/ShortTermOperatingReserves/ShortTermOperatingReserves";
import SoSoTradePrices from "pages/SoSoTradePrices/SoSoTradePrices";
import SurplusAndMargin from "pages/SurplusAndMargin/SurplusAndMargin";
import SystemSellBuyPrices from "pages/SystemSellBuyPrices/SystemSellBuyPrices";
import Temperature from "pages/Temperature/Temperature";
import TotalLoadActualAndDayAhead from "pages/TotalLoadActualAndDayAhead/TotalLoadActualAndDayAhead";
import TotalLoadWeekAhead from "pages/TotalLoadWeekAhead/TotalLoadWeekAhead";
import Transmission from "pages/Transmission/Transmission";
import WindGeneration from "pages/WindGeneration/WindGeneration";
import { FC, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import SilentLogin from "utils/SilentLogin/silentLogin";
import featureFlags from "utils/featureFlags";
import routeConstants from "utils/routeConstants";

const ApiDocumentation = lazy(
  () => import("pages/ApiDocumentation/ApiDocumentation")
);

interface OptionalAuthProviderProps {
  authContext?: IPublicClientApplication;
  children: JSX.Element;
}

const OptionalAuthProvider: FC<OptionalAuthProviderProps> = ({
  authContext,
  children,
}) => {
  if (authContext) {
    return <MsalProvider instance={authContext}>{children}</MsalProvider>;
  }
  return <>{children}</>;
};

interface AppProps {
  authContext?: IPublicClientApplication;
}

const App: FC<AppProps> = ({ authContext }) => (
  <AppInsightsContext.Provider value={reactPlugin}>
    <OptionalAuthProvider authContext={authContext}>
      <HelmetProvider>
        <GlobalMeta />
        <Router>
          <ErrorProvider>
            <>
              <ScrollToTop />
              <Layout>
                <Routes>
                  <Route path={routeConstants.home} element={<Homepage />} />
                  <Route
                    path={routeConstants.generation}
                    element={<Generation />}
                  />
                  <Route path={routeConstants.demand} element={<Demand />} />
                  <Route
                    path={routeConstants.balancing}
                    element={<Balancing />}
                  />
                  <Route path={routeConstants.notices} element={<Notices />} />
                  <Route
                    path={routeConstants.transmission}
                    element={<Transmission />}
                  />
                  <Route
                    path={routeConstants.dataServices}
                    element={<DataServices />}
                  />
                  {featureFlags.accountArea && (
                    <Route
                      path={routeConstants.myInsights}
                      element={<MyInsights />}
                    />
                  )}
                  <Route
                    path={routeConstants.generationByFuelType}
                    element={<GenerationByFuelType />}
                  />
                  <Route
                    path={routeConstants.generationForecast}
                    element={<GenerationForecast />}
                  />
                  <Route
                    path={routeConstants.generationForecastForWindAndSolar}
                    element={<GenerationForecastForWindAndSolar />}
                  />
                  <Route
                    path={routeConstants.interconnectorFlows}
                    element={<InterconnectorFlows />}
                  />
                  <Route
                    path={routeConstants.rollingSystemDemand}
                    element={<RollingSystemDemand />}
                  />
                  <Route
                    path={routeConstants.surplusAndMargin}
                    element={<SurplusAndMargin />}
                  />
                  <Route
                    path={routeConstants.demandForecast}
                    element={<DemandForecast />}
                  />
                  <Route
                    path={routeConstants.shortTermOperatingReserves}
                    element={<ShortTermOperatingReserves />}
                  />
                  <Route
                    path={routeConstants.rollingSystemFrequency}
                    element={<RollingSystemFrequency />}
                  />
                  <Route
                    path={routeConstants.temperature}
                    element={<Temperature />}
                  />
                  <Route
                    path={routeConstants.marketIndexPrices}
                    element={<MarketIndexPrices />}
                  />
                  <Route
                    path={routeConstants.demandOutturn}
                    element={<DemandOutturn />}
                  />
                  <Route
                    path={routeConstants.dailyEnergyTransmitted}
                    element={<DailyEnergyTransmitted />}
                  />
                  <Route
                    path={routeConstants.indicatedForecasts}
                    element={<IndicatedForecasts />}
                  />
                  <Route
                    path={routeConstants.windGeneration}
                    element={<WindGeneration />}
                  />
                  <Route
                    path={routeConstants.actualGenerationPerType}
                    element={<ActualGenerationPerType />}
                  />
                  <Route
                    path={
                      routeConstants.actualOrEstimatedWindAndSolarPowerGeneration
                    }
                    element={<ActualOrEstimatedWindAndSolarPowerGeneration />}
                  />
                  <Route
                    path={routeConstants.dayAheadAggregatedGeneration}
                    element={<DayAheadAggregatedGeneration />}
                  />
                  <Route
                    path={routeConstants.totalLoadActualAndDayAhead}
                    element={<TotalLoadActualAndDayAhead />}
                  />
                  <Route
                    path={routeConstants.totalLoadWeekAhead}
                    element={<TotalLoadWeekAhead />}
                  />
                  <Route path={routeConstants.bmuView} element={<BmuView />} />
                  <Route
                    path={routeConstants.marketView}
                    element={<MarketView />}
                  />
                  <Route
                    path={routeConstants.adjustmentDataNetbsad}
                    element={<AdjustmentDataNetbsad />}
                  />
                  <Route
                    path={routeConstants.creditDefaultNotices}
                    element={<CreditDefaultNotices />}
                  />
                  <Route
                    path={routeConstants.adjustmentActionsDisbsad}
                    element={<AdjustmentActionsDisbsad />}
                  />
                  <Route path={routeConstants.lolpdrm} element={<Lolpdrm />} />
                  <Route path={routeConstants.remit} element={<Remit />} />
                  <Route
                    path={`${routeConstants.remitMessageDetails}/:mrid`}
                    element={<RemitMessageDetails />}
                  />
                  <Route
                    path={routeConstants.remitSubmit}
                    element={<RemitSubmit />}
                  />
                  <Route
                    path={routeConstants.indicativeDemandPeak}
                    element={<IndicativeDemandPeak />}
                  />
                  <Route
                    path={routeConstants.restorationZoneDemandForecast}
                    element={<RestorationZoneDemandForecast />}
                  />
                  <Route
                    path={routeConstants.restorationZoneDemandRestored}
                    element={<RestorationZoneDemandRestored />}
                  />
                  <Route path={routeConstants.events} element={<Events />} />
                  <Route
                    path={routeConstants.sosoTradePrices}
                    element={<SoSoTradePrices />}
                  />
                  <Route
                    path={`${routeConstants.apiDocs}/*`}
                    element={
                      <SuspenseWrapper>
                        <ApiDocumentation />
                      </SuspenseWrapper>
                    }
                  />
                  <>
                    <Route
                      path={routeConstants.settlementMessages}
                      element={<SettlementMessages />}
                    />
                    <Route
                      path={routeConstants.derivedData}
                      element={<DerivedData />}
                    />
                    <Route
                      path={routeConstants.systemPrices}
                      element={<SystemSellBuyPrices />}
                    />
                    <Route
                      path={routeConstants.detailedSystemPrices}
                      element={<DetailedSystemPrices />}
                    />
                  </>
                  {featureFlags.accountArea && (
                    <Route
                      path={routeConstants.profile}
                      element={<Profile />}
                    />
                  )}
                  <Route path={routeConstants.iris} element={<Iris />} />
                  <Route
                    path={routeConstants.login}
                    element={<SilentLogin />}
                  />
                  <Route
                    path="/whats-new/*"
                    element={<Navigate to={routeConstants.home} replace />}
                  />
                  <Route
                    path="/whatsnew/*"
                    element={<Navigate to={routeConstants.home} replace />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Layout>
            </>
          </ErrorProvider>
        </Router>
      </HelmetProvider>
    </OptionalAuthProvider>
  </AppInsightsContext.Provider>
);

export default App;
